import { createContext, useContext, useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { MOCK_PROMPTS, MOCK_VERSIONS, type Prompt, type PromptVersion } from './mockData'

/** Editable fields of a prompt (the Prompts-tab form state). */
export type PromptDraft = {
  name: string
  description: string
  body: string
}

/**
 * Session-wide prototype state for the Ops → Prompts page. Mounted once by
 * `OpsPromptsLayoutComponent` so the whole page reads and mutates ONE in-memory
 * store. A prompt has two version pointers — `activeVersion` (used by the
 * pipeline) and `draftVersion` (latest edit) — that can match. **Saving creates a
 * new draft version** (it does NOT go live); **activating** points `activeVersion`
 * at the draft. Nothing persists (TRK-5281 is frontend-only); this provider is the
 * single seam to swap for real GraphQL later.
 */
export type OpsPromptsContextValue = {
  prompts: Prompt[]
  versions: PromptVersion[]
  /** Save the draft as a NEW version and point `draftVersion` at it (not active). */
  savePrompt: (id: string, draft: PromptDraft) => void
  /** Set `activeVersion` to the current `draftVersion` (make the draft live). */
  activateDraft: (id: string) => void
}

const OpsPromptsContext = createContext<OpsPromptsContextValue | null>(null)

export function OpsPromptsProvider({ children }: { children: ReactNode }): ReactElement {
  const [prompts, setPrompts] = useState<Prompt[]>(MOCK_PROMPTS)
  const [versions, setVersions] = useState<PromptVersion[]>(MOCK_VERSIONS)

  const value = useMemo<OpsPromptsContextValue>(() => {
    const savePrompt = (id: string, draft: PromptDraft) => {
      const prompt = prompts.find((item) => item.id === id)
      if (!prompt) return
      const now = new Date().toISOString()
      // Every save appends a new draft version above the current highest.
      const highest = versions.reduce((max, v) => (v.promptId === id && v.version > max ? v.version : max), 0)
      const nextVersion = highest + 1
      setPrompts((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                name: draft.name,
                description: draft.description,
                draftVersion: nextVersion,
                updatedBy: `You`,
                updatedAt: now,
              }
            : item,
        ),
      )
      setVersions((prev) => [
        {
          id: `ver_local_${now}`,
          promptId: id,
          promptKey: prompt.key,
          promptName: draft.name,
          version: nextVersion,
          editor: `You`,
          savedAt: now,
          changeSummary: `Saved draft from the Prompts editor.`,
          body: draft.body,
        },
        ...prev,
      ])
    }

    const activateDraft = (id: string) => {
      setPrompts((prev) => prev.map((item) => (item.id === id ? { ...item, activeVersion: item.draftVersion } : item)))
    }

    return { prompts, versions, savePrompt, activateDraft }
  }, [prompts, versions])

  return <OpsPromptsContext.Provider value={value}>{children}</OpsPromptsContext.Provider>
}

export function useOpsPromptsData(): OpsPromptsContextValue {
  const value = useContext(OpsPromptsContext)
  if (!value) {
    throw new Error(`useOpsPromptsData must be used within an OpsPromptsProvider`)
  }
  return value
}
